// Import from 3CX — a 4-step wizard that turns a 3CX backup (.zip) into a new
// Stratus tenant. Upload → choose departments → convert → report.
//
// Backend contract:
//   POST /api/import/3cx            multipart, field `backup` (.zip)     → { job_uuid }
//   GET  /api/import/3cx/:job                                            → { status, departments, report, error }
//   GET  /api/import/3cx/:job/validate?groups=a,b                       → { blockers, blocker_count, warning_count }
//   POST /api/import/3cx/:job/run   { groups, skip_ai_agents, verbatim_secrets, renumber_map } → { ok }
// status ∈ uploaded | parsing | parsed | running | done | error.
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  FileUp, Upload, Loader2, Check, CheckCircle2, AlertTriangle, ArrowRight,
  Users, Phone, Radio, Cable, Hash, Music, FlaskConical, RotateCcw, ListChecks,
  Zap, X, RefreshCw,
} from 'lucide-react';
import { getToken } from '../lib/api';

type JobStatus = 'uploaded' | 'parsing' | 'parsed' | 'running' | 'done' | 'error';

type Department = { group_code: string; name: string; extension_count: number };

// The report shape is intentionally loose — we read the counts we know about and
// tolerate anything else the backend adds. skipped may be a number or a list.
// Matches what api/routes/import_3cx.js runEtl writes: counts nested under
// `totals`, plus an `errors` list of { at, error }.
type ImportTotals = {
  extensions_created?: number;
  extensions_skipped?: number;
  voicemails?: number;
  devices?: number;
  ring_groups?: number;
  gateways?: number;
  inbound_rules?: number;
  outbound_rules?: number;
  files_copied?: number;
};
type ImportError = { at?: string; error?: string };
type ImportedDomain = { group_code: string; domain_uuid: string; domain_name: string };

type ImportReport = {
  totals?: ImportTotals;
  errors?: ImportError[];
  domains?: ImportedDomain[];
  per_group?: unknown[];
  parse?: unknown;
  [k: string]: unknown;
};

type Blocker = {
  type: string;
  severity: 'block' | 'warn';
  group?: string;
  extension?: string;
  suggested_renumber?: string;
  mac?: string;
  did?: string;
  reason?: string;
};
type DidEntry = { did: string; destination?: string; destination_type?: string; external_line_id?: string; trunk_name?: string; trunk_host?: string };
type ValidateGroup = { code: string; name: string; extensions: number; phones: number; dids: number; did_list?: DidEntry[] };
type ValidationResult = {
  blockers: Blocker[];
  blocker_count: number;
  warning_count: number;
  groups?: ValidateGroup[];
  note?: string;
};
type RenumberedEntry = { from: string; to: string; name: string; macs: string[]; group: string; domain_name: string };

type GatewayRow = { gateway_uuid: string; gateway_name: string; gateway: string; domain_uuid?: string | null; itemType?: string };

type CutoverResult = { ok: boolean; phones_reproved: number; inbound_rules_activated: number; outbound_rules_activated: number };
type HistoryDomain = { domain_uuid: string; domain_name: string; extensions: number; dids: number; outbound_rules: number; phones: number; cut_over: boolean; gateways: GatewayRow[] };

type Job = {
  status: JobStatus;
  departments?: Department[];
  report?: ImportReport | null;
  error?: string | null;
};

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error((await r.text()) || `${r.status} ${r.statusText}`);
  return r.json() as Promise<T>;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

const STEPS = ['Upload', 'Departments', 'Convert', 'Report'] as const;

export default function Import3cx() {
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 });
  const [drag, setDrag] = useState(false);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [skipAi, setSkipAi] = useState(true);       // skip AI-agent extensions
  const [verbatim, setVerbatim] = useState(true);   // keep original SIP passwords
  const [starting, setStarting] = useState(false);
  const [sbcNodes, setSbcNodes] = useState<{ id: string; name: string }[]>([]);
  const [sbcByGroup, setSbcByGroup] = useState<Record<string, string>>({});   // group_code -> sbc_node_id

  // Validation + renumber + DID selection state
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [renumberMap, setRenumberMap] = useState<Record<string, string>>({});       // old_ext -> new_ext
  const [selectedDids, setSelectedDids] = useState<Set<string>>(new Set());         // DIDs to import (all pre-checked)
  const [allDids, setAllDids] = useState<DidEntry[]>([]);                           // full DID list from validate

  // Cutover dialog state
  const [cutoverDomain, setCutoverDomain] = useState<ImportedDomain | null>(null);
  const [trunks, setTrunks] = useState<GatewayRow[]>([]);
  const [cutoverGateway, setCutoverGateway] = useState('');
  const [cutoverTrunk, setCutoverTrunk] = useState('');
  const [cutoverLoading, setCutoverLoading] = useState(false);
  const [cutoverResults, setCutoverResults] = useState<Record<string, CutoverResult>>({});
  const [cutoverErr, setCutoverErr] = useState<string | null>(null);
  const [phoneSbc, setPhoneSbc] = useState<{ total: number; unregistered: number; by_sbc: { sbc_node_id: string | null; sbc_name: string | null; count: number }[] } | null>(null);
  // '' = auto-detect per phone (recommended) · '__local__' = force direct · uuid = force that SBC
  const [cutoverMode, setCutoverMode] = useState('');

  // The SBC each department's phones will register through (bound at import; the phones
  // are reprovisioned to it at cutover). Optional — no SBC = direct / subnet-inferred.
  useEffect(() => {
    fetch('/api/sbc/nodes', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        const list = Array.isArray(rows) ? rows : (rows?.nodes || rows?.data || []);
        setSbcNodes(list.map((n: { id: string; name: string }) => ({ id: n.id, name: n.name })));
      })
      .catch(() => {});
  }, []);

  // Persistent import history — fetched from DB on mount so prior imports survive
  // browser navigation / session loss.
  const [historyDomains, setHistoryDomains] = useState<HistoryDomain[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const refreshHistory = () => {
    fetch('/api/import/3cx/tenants', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => setHistoryDomains(Array.isArray(rows) ? rows : []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  };
  useEffect(refreshHistory, []);

  const status = job?.status;

  // Poll the job while it's alive. parsed is not terminal — we keep polling so a
  // run() started from step 2 is picked up as running → done without re-arming.
  useEffect(() => {
    if (!jobUuid) return;
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const j = await getJson<Job>(`/api/import/3cx/${jobUuid}`);
        if (!active) return;
        setJob(j);
        if (j.status === 'done') {
          // Fire-and-forget: delete the .zip from disk now that the import is complete.
          fetch(`/api/import/3cx/${jobUuid}/cleanup`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${getToken()}` },
          }).catch(() => { /* best-effort — non-fatal */ });
        } else if (j.status !== 'error') {
          timer = window.setTimeout(poll, j.status === 'running' ? 1000 : 2000);
        }
      } catch (e) {
        if (!active) return;
        setJob({ status: 'error', error: (e as Error)?.message || 'Lost contact with the import job.' });
      }
    };
    poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [jobUuid]);

  // Derived wizard phase (1-4). `starting` pins us on step 3 even if a poll that
  // was already in flight briefly reports the pre-run `parsed` status.
  const phase: 1 | 2 | 3 | 4 =
    status === 'done' ? 4
    : (starting || status === 'running') ? 3
    : status === 'parsed' ? 2
    : 1;

  const isError = status === 'error';

  function upload(file: File) {
    if (!/\.zip$/i.test(file.name)) { setUploadErr('The 3CX backup must be a .zip file.'); return; }
    setUploadErr(null);
    setUploading(true);
    setUploadPct(0);
    setUploadBytes({ loaded: 0, total: file.size });

    // XHR (not fetch) so we get real upload-progress events — a 3CX backup is
    // often > 1 GB, so a live percentage matters.
    const fd = new FormData();
    fd.append('backup', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/import/3cx');
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    // NB: do NOT set Content-Type — the browser adds the multipart boundary.
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      setUploadPct(Math.round((e.loaded / e.total) * 100));
      setUploadBytes({ loaded: e.loaded, total: e.total });
    };
    xhr.upload.onload = () => setUploadPct(100);
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const j = JSON.parse(xhr.responseText) as { job_uuid?: string };
          if (!j.job_uuid) { setUploadErr('The server did not return a job id.'); return; }
          setJob({ status: 'uploaded' });
          setJobUuid(j.job_uuid); // arms the poll effect
        } catch { setUploadErr('Unexpected server response.'); }
      } else {
        setUploadErr(xhr.responseText || `${xhr.status} ${xhr.statusText}`);
      }
    };
    xhr.onerror = () => { setUploading(false); setUploadErr('Upload failed — network error.'); };
    xhr.onabort = () => { setUploading(false); };
    xhr.send(fd);
  }

  // Step 1: validate selected groups for collisions; if any extension_collision
  // blockers found, show the renumber panel. For v18 backups (note returned) or
  // if there are no blockers, proceed straight to run.
  async function validateAndRun() {
    const groups = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!groups.length || !jobUuid) return;
    setValidating(true);
    setValidationResult(null);
    setRenumberMap({});
    setAllDids([]);
    setSelectedDids(new Set());
    try {
      const v = await getJson<ValidationResult>(
        `/api/import/3cx/${jobUuid}/validate?groups=${groups.join(',')}`
      );
      const collisions = v.blockers.filter(b => b.type === 'extension_collision');

      // Collect all DIDs across groups so the operator can review / deselect.
      const dids: DidEntry[] = [];
      for (const g of (v.groups || [])) {
        for (const d of (g.did_list || [])) dids.push(d);
      }
      const allSelected = new Set(dids.map(d => d.did));

      if (v.note && collisions.length === 0 && dids.length === 0) {
        // v18 backup — nothing to review, run immediately.
        setValidating(false);
        await run({}, null);
        return;
      }

      // Pre-fill renumber map from suggestions.
      const suggested: Record<string, string> = {};
      for (const b of collisions) {
        if (b.extension && b.suggested_renumber) suggested[b.extension] = b.suggested_renumber;
      }
      setRenumberMap(suggested);
      setAllDids(dids);
      setSelectedDids(allSelected);
      setValidationResult(v);
      setValidating(false);
    } catch (_e) {
      setValidating(false);
      await run({}, null);
    }
  }

  async function run(rmap: Record<string, string>, dids: Set<string> | null) {
    const groups = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!groups.length || !jobUuid) return;
    setStarting(true);
    setValidationResult(null);
    try {
      const body: Record<string, unknown> = {
        groups,
        skip_ai_agents: skipAi,
        verbatim_secrets: verbatim,
        sbc_by_group: sbcByGroup,
        renumber_map: rmap,
      };
      // null = no filter (legacy / v18). Explicit set = operator-reviewed list.
      if (dids !== null) body.selected_dids = [...dids];
      const r = await fetch(`/api/import/3cx/${jobUuid}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.text()) || `${r.status} ${r.statusText}`);
      setJob(j => (j ? { ...j, status: 'running' } : { status: 'running' }));
    } catch (e) {
      setStarting(false);
      setJob(j => ({ ...(j || { status: 'error' }), status: 'error', error: (e as Error)?.message || 'Failed to start the conversion.' }));
    }
  }

  function reset() {
    setJobUuid(null);
    setJob(null);
    setUploadErr(null);
    setUploading(false);
    setUploadPct(0);
    setUploadBytes({ loaded: 0, total: 0 });
    setSelected({});
    setSbcByGroup({});
    setSkipAi(true);
    setVerbatim(true);
    setStarting(false);
    setValidating(false);
    setValidationResult(null);
    setRenumberMap({});
    setAllDids([]);
    setSelectedDids(new Set());
    setCutoverDomain(null);
    setCutoverResults({});
  }

  function openCutover(domain: ImportedDomain, prefetchedGateways?: GatewayRow[]) {
    setCutoverDomain(domain);
    setCutoverGateway('');
    setCutoverTrunk('');
    setCutoverErr(null);
    setPhoneSbc(null);
    setCutoverMode('');
    if (prefetchedGateways) {
      setTrunks(prefetchedGateways);
    } else {
      // Fetch trunks lazily — only when the dialog opens (session-based flow).
      fetch('/api/trunks', { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => (r.ok ? r.json() : []))
        .then((rows: GatewayRow[]) => {
          // Import placeholders (import.invalid / sink.invalid) are what cutover REBINDS
          // AWAY FROM — offering them as the target would no-op the DID activation.
          const gwRows = rows.filter(r =>
            (r.itemType === 'trunk' || (!r.itemType && r.gateway_uuid)) &&
            r.gateway !== 'import.invalid' && r.gateway !== 'sink.invalid');
          setTrunks(gwRows);
        })
        .catch(() => setTrunks([]));
    }
    fetch(`/api/import/3cx/tenants/${domain.domain_uuid}/phones`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => setPhoneSbc(data))
      .catch(() => {});
  }

  async function executeCutover() {
    if (!cutoverDomain || !cutoverGateway) return;
    setCutoverLoading(true);
    setCutoverErr(null);
    try {
      const r = await fetch(`/api/import/3cx/tenants/${cutoverDomain.domain_uuid}/cutover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          gateway_uuid: cutoverGateway,
          trunk_uuid: cutoverTrunk || cutoverGateway,
          // omitted = backend auto-detects per phone from stratus_phone_inventory;
          // '__local__' / uuid = operator override forcing every phone one way
          ...(cutoverMode ? { sbc_node_id: cutoverMode } : {}),
        }),
      });
      if (!r.ok) throw new Error((await r.text()) || `${r.status} ${r.statusText}`);
      const result = (await r.json()) as CutoverResult;
      setCutoverResults(prev => ({ ...prev, [cutoverDomain.domain_uuid]: result }));
      setCutoverDomain(null);
      refreshHistory();
    } catch (e) {
      setCutoverErr((e as Error)?.message || 'Cutover failed.');
    } finally {
      setCutoverLoading(false);
    }
  }

  const departments = job?.departments || [];
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><Cable size={18} /> Import from 3CX</h1>
        <p className="text-textSecondary text-sm">Convert a 3CX backup into a new Stratus tenant.</p>
      </div>

      {/* Beta banner */}
      <div className="card border-primary/30 bg-primary/5 p-3 text-sm text-textSecondary flex items-center gap-2">
        <FlaskConical size={16} className="text-primary shrink-0" />
        <span><strong className="text-textPrimary">Beta</strong> — converts a 3CX backup into a new Stratus tenant.</span>
      </div>

      <Stepper phase={phase} />

      {/* Error surfaces above the step body at any phase. */}
      {isError && (
        <div className="card border-danger/40 bg-danger/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-danger">The import failed</div>
            <div className="text-textSecondary break-words">{job?.error || 'The import job reported an error.'}</div>
          </div>
          <button onClick={reset} className="btn-ghost inline-flex items-center gap-1 text-sm shrink-0">
            <RotateCcw size={14} /> Start over
          </button>
        </div>
      )}

      {/* ── Step 1: Upload ─────────────────────────────────────────────── */}
      {!isError && phase === 1 && (
        uploading ? (
          <ProgressGauge
            pct={uploadPct}
            title="Uploading the backup…"
            sub={uploadBytes.total ? `${fmtBytes(uploadBytes.loaded)} of ${fmtBytes(uploadBytes.total)}` : 'Sending…'}
          />
        ) : (jobUuid && (status === 'uploaded' || status === 'parsing')) ? (
          <WorkingBar
            title="Reading the backup…"
            steps={[
              'Unpacking the archive…',
              'Restoring the 3CX configuration database…',
              'Inventorying departments and extensions…',
              'Almost there — reading the phone book…',
            ]}
          />
        ) : (
          <div className="space-y-3">
            <div
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => {
                e.preventDefault(); setDrag(false);
                const f = e.dataTransfer.files?.[0];
                if (f) upload(f);
              }}
              className={`card p-8 border-2 border-dashed transition-colors text-center ${drag ? 'border-primary bg-primary/10' : 'border-border'}`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-full bg-primary/15 p-3 text-primary"><FileUp size={28} /></div>
                <div>
                  <div className="font-semibold text-base">Upload a 3CX backup</div>
                  <div className="text-sm text-textSecondary">
                    Drag a <code className="bg-elevated px-1 rounded">.zip</code> here, or browse for it. Nothing is created until you pick departments and convert.
                  </div>
                </div>
                <label className="btn-primary inline-flex items-center gap-2 px-4 py-2 cursor-pointer">
                  <Upload size={16} /> {uploading ? 'Uploading…' : 'Browse…'}
                  <input
                    type="file"
                    accept=".zip"
                    hidden
                    disabled={uploading}
                    onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
                  />
                </label>
              </div>
            </div>
            {uploadErr && (
              <div className="card border-danger/40 bg-danger/10 p-3 flex items-start gap-2 text-sm">
                <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                <span className="text-textSecondary break-words">{uploadErr}</span>
              </div>
            )}
          </div>
        )
      )}

      {/* ── Step 2: Choose departments ─────────────────────────────────── */}
      {!isError && phase === 2 && (
        <div className="space-y-4">
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold flex items-center gap-2"><ListChecks size={16} /> Departments to convert</div>
              <span className="text-xs text-textSecondary">{selectedCount} of {departments.length} selected</span>
            </div>
            {departments.length === 0 ? (
              <div className="text-sm text-textSecondary">No departments were found in this backup.</div>
            ) : (
              <div className="space-y-2">
                {departments.map(d => (
                  <label
                    key={d.group_code}
                    className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-elevated cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[d.group_code]}
                      onChange={e => setSelected(s => ({ ...s, [d.group_code]: e.target.checked }))}
                    />
                    <span className="flex-1 font-medium">{d.name || d.group_code}</span>
                    {/* Always offered when selected — Auto-detect vs forced-Local is a real
                        choice even with zero SBCs registered (SBCs installed later, before
                        cutover, are picked up per phone by auto-detect). */}
                    {selected[d.group_code] && (
                      <select
                        className="text-xs bg-elevated border border-border rounded px-1.5 py-1"
                        value={sbcByGroup[d.group_code] || ''}
                        onClick={e => e.preventDefault()}
                        onChange={e => setSbcByGroup(s => ({ ...s, [d.group_code]: e.target.value }))}
                        title="How this department's phones find their SBC. Auto-detect resolves each phone individually at cutover from the phone inventory — right for multi-location departments."
                      >
                        <option value="">Auto-detect at cutover (recommended)</option>
                        <option value="__local__">Local — no SBC (force all)</option>
                        {sbcNodes.map(n => <option key={n.id} value={n.id}>Force all → {n.name}</option>)}
                      </select>
                    )}
                    <span className="badge bg-elevated text-textSecondary">{num(d.extension_count)} ext</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="card p-4 space-y-3">
            <div className="font-semibold">Options</div>
            <ToggleRow
              checked={skipAi}
              onChange={setSkipAi}
              title="Skip AI-agent extensions"
              desc="Leave 3CX AI-agent / bot extensions behind — import only real people and devices."
            />
            <ToggleRow
              checked={verbatim}
              onChange={setVerbatim}
              title="Keep original SIP passwords"
              desc="Re-use each extension's existing SIP secret so the phones re-register without any reconfiguration."
            />
          </div>

          {/* Review panel — shown after validate; always appears when there are conflicts or DIDs */}
          {validationResult && (() => {
            const collisions = validationResult.blockers.filter(b => b.type === 'extension_collision');
            const hasReview = collisions.length > 0 || allDids.length > 0;
            if (!hasReview) {
              // Nothing to review — proceed immediately
              run(renumberMap, selectedDids);
              return null;
            }
            return (
              <div className="space-y-3">
                {collisions.length > 0 && (
                  <RenumberPanel
                    blockers={collisions}
                    renumberMap={renumberMap}
                    onChange={setRenumberMap}
                  />
                )}
                {allDids.length > 0 && (
                  <DidsPanel
                    dids={allDids}
                    selected={selectedDids}
                    onChange={setSelectedDids}
                  />
                )}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => { setValidationResult(null); setAllDids([]); setSelectedDids(new Set()); }}
                    disabled={starting}
                    className="btn-ghost text-sm"
                  >
                    Back
                  </button>
                  <div className="flex items-center gap-2">
                    {collisions.length > 0 && (
                      <button
                        onClick={() => run({}, selectedDids)}
                        disabled={starting}
                        className="btn-ghost text-sm inline-flex items-center gap-1 text-textSecondary"
                        title="Skip conflicting extensions instead of renumbering them"
                      >
                        Skip conflicts
                      </button>
                    )}
                    <button
                      onClick={() => run(renumberMap, selectedDids)}
                      disabled={starting || (collisions.length > 0 && collisions.some(b => !renumberMap[b.extension!]?.trim()))}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      {starting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                      {starting ? 'Importing…' : `Run import${selectedDids.size < allDids.length ? ` (${selectedDids.size} of ${allDids.length} DIDs)` : ''}`}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {!validationResult && (
            <div className="flex justify-end">
              <button
                onClick={validateAndRun}
                disabled={selectedCount < 1 || starting || validating}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {validating ? <Loader2 size={15} className="import-spinner" /> : <ArrowRight size={15} />}
                {validating ? 'Checking…' : 'Convert selected'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Running ────────────────────────────────────────────── */}
      {!isError && phase === 3 && (() => {
        const liveReport = job?.report || {};
        const lt = liveReport.totals || {};
        const liveCount = num(lt.extensions_created) + num(lt.ring_groups) + num(lt.devices);
        return (
          <WorkingBar
            title={liveCount > 0 ? `Converting… (${liveCount} object${liveCount === 1 ? '' : 's'} created so far)` : 'Converting to Stratus…'}
            steps={[
              'Creating extensions and SIP credentials…',
              'Registering phones and devices…',
              'Building ring groups and call flows…',
              'Restoring voicemail boxes and PINs…',
              'Copying audio — greetings, prompts, recordings…',
              'Leave this page open — finishing up…',
            ]}
          >
            {liveCount > 0 && <ReportGrid report={liveReport} />}
          </WorkingBar>
        );
      })()}

      {/* ── Step 4: Report ─────────────────────────────────────────────── */}
      {!isError && phase === 4 && (() => {
        const r = job?.report || {};
        const t = r.totals || {};
        const totalImported = num(t.extensions_created) + num(t.ring_groups) + num(t.inbound_rules);
        const hasErrors = (Array.isArray(r.errors) && r.errors.length > 0) ||
          (Array.isArray(r.per_group) && (r.per_group as Array<{ errors?: unknown[] }>).some(g => g.errors && (g.errors as unknown[]).length > 0));
        const blocked = totalImported === 0 && hasErrors;
        return (
        <div className="space-y-4">
          <div className="card p-4 space-y-4">
            <div className="flex items-center gap-2">
              {blocked
                ? <AlertTriangle size={20} className="text-danger" />
                : totalImported === 0
                ? <AlertTriangle size={20} className="text-warning" />
                : <CheckCircle2 size={20} className="text-success" />}
              <div className="font-semibold">
                {blocked ? 'Import blocked — nothing was created'
                  : totalImported === 0 ? 'Import complete — nothing to import'
                  : 'Import complete'}
              </div>
            </div>
            <ReportGrid report={r} />
            <ReportExtras report={r} />
          </div>

          {/* ── Reprovision list — phones that need re-push because their extension changed ── */}
          {Array.isArray((r as { renumbered?: RenumberedEntry[] }).renumbered) &&
            ((r as { renumbered?: RenumberedEntry[] }).renumbered || []).length > 0 && (
            <ReprovisionList entries={(r as { renumbered?: RenumberedEntry[] }).renumbered!} />
          )}

          {/* ── Cut Over panel — one row per imported domain ── */}
          {(job?.report?.domains || []).length > 0 && (
            <div className="card p-4 space-y-3">
              <div className="font-semibold flex items-center gap-2"><Zap size={16} className="text-warning" /> Cut Over</div>
              <div className="text-sm text-textSecondary">
                Activate live carrier routing for each imported department. Phones are reprovisioned through the selected SBC.
                <strong className="text-textPrimary"> This cannot be undone without manual reversal.</strong>
              </div>
              <div className="space-y-2">
                {(job?.report?.domains || []).map(dom => {
                  const done = cutoverResults[dom.domain_uuid];
                  return (
                    <div key={dom.domain_uuid} className="flex items-center justify-between rounded-md border border-border bg-elevated px-3 py-2">
                      <div className="text-sm">
                        <span className="font-medium">{dom.domain_name}</span>
                        {done && (
                          <span className="ml-2 text-xs text-success">
                            Live — {done.inbound_rules_activated} DIDs, {done.outbound_rules_activated} outbound routes, {done.phones_reproved} phone{done.phones_reproved === 1 ? '' : 's'} queued
                          </span>
                        )}
                      </div>
                      {/* The button stays available even after a tenant is live: cut-over
                          is safely re-runnable (re-enables the trunk, re-binds DIDs, re-queues
                          phone provisioning) — needed to converge phones that didn't come over
                          the first time, or to re-apply after an SBC/phone fix deploys. */}
                      <div className="flex items-center gap-2 shrink-0">
                        {done && <CheckCircle2 size={16} className="text-success" />}
                        <button
                          onClick={() => openCutover(dom)}
                          className="btn-ghost text-xs inline-flex items-center gap-1 text-warning hover:text-warning"
                          title={done ? 'Re-run cut over — re-applies routing and re-provisions any phones that have not registered yet' : 'Cut over this department to live carrier routing'}
                        >
                          <Zap size={13} /> {done ? 'Re-run' : 'Cut over'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between pt-1">
              <Link to="/users" className="btn-primary inline-flex items-center gap-2">
                <Users size={15} /> Open the Users list
              </Link>
              <button onClick={reset} className="btn-ghost inline-flex items-center gap-1 text-sm">
                <RotateCcw size={14} /> Import another
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Import History — persistent list from DB, survives navigation ── */}
      {(historyLoading || historyDomains.length > 0) && (
        <div className="card p-4 space-y-3">
          <div className="font-semibold flex items-center gap-2">
            <Zap size={16} className="text-warning" /> Cut Over
          </div>
          <div className="text-sm text-textSecondary">
            Activate live carrier routing for each imported department. Phones are reprovisioned through the selected SBC.
            <strong className="text-textPrimary"> This cannot be undone without manual reversal.</strong>
          </div>
          {historyLoading ? (
            <div className="text-sm text-textSecondary">Loading...</div>
          ) : (
            <div className="space-y-2">
              {historyDomains.map(dom => {
                const sessionResult = cutoverResults[dom.domain_uuid];
                const isCutOver = dom.cut_over || !!sessionResult;
                return (
                  <div key={dom.domain_uuid} className="flex items-center justify-between rounded-md border border-border bg-elevated px-3 py-2">
                    <div className="text-sm">
                      <span className="font-medium">{dom.domain_name}</span>
                      <span className="ml-2 text-xs text-textSecondary">
                        {dom.dids} DID{dom.dids === 1 ? '' : 's'} · {dom.outbound_rules} outbound route{dom.outbound_rules === 1 ? '' : 's'} · {dom.phones} phone{dom.phones === 1 ? '' : 's'}
                      </span>
                      {sessionResult && (
                        <span className="ml-2 text-xs text-success">
                          Live — {sessionResult.inbound_rules_activated} DIDs, {sessionResult.outbound_rules_activated} outbound routes, {sessionResult.phones_reproved} phone{sessionResult.phones_reproved === 1 ? '' : 's'} queued
                        </span>
                      )}
                      {isCutOver && !sessionResult && (
                        <span className="ml-2 text-xs text-success">Live — cut over</span>
                      )}
                    </div>
                    {/* Cut-over is idempotent, so keep the button available even once live —
                        needed to converge phones that didn't register the first time or to
                        re-apply after a fix deploys. Show 'Re-run' next to the live check. */}
                    <div className="flex items-center gap-2 shrink-0">
                      {isCutOver && <CheckCircle2 size={16} className="text-success" />}
                      <button
                        onClick={() => openCutover(
                          { group_code: '', domain_uuid: dom.domain_uuid, domain_name: dom.domain_name },
                          dom.gateways.filter(g => g.gateway !== 'import.invalid' && g.gateway !== 'sink.invalid'),
                        )}
                        className="btn-ghost text-xs inline-flex items-center gap-1 text-warning hover:text-warning"
                        title={isCutOver ? 'Re-run cut over — re-applies routing and re-provisions any phones that have not registered yet' : 'Cut over this department to live carrier routing'}
                      >
                        <Zap size={13} /> {isCutOver ? 'Re-run' : 'Cut over'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Cutover confirmation dialog ─────────────────────────────────── */}
      {cutoverDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-md space-y-4 p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 font-semibold">
                <Zap size={18} className="text-warning" /> Cut over {cutoverDomain.domain_name}
              </div>
              <button onClick={() => setCutoverDomain(null)} disabled={cutoverLoading} className="btn-ghost p-1">
                <X size={16} />
              </button>
            </div>

            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-textSecondary">
              This will activate <strong className="text-textPrimary">live carrier routing</strong> for{' '}
              <strong className="text-textPrimary">{cutoverDomain.domain_name}</strong>.
              Phones will be reprovisioned on the Stratus SBC.
              <br /><strong className="text-warning">This cannot be undone without manual reversal.</strong>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Inbound gateway <span className="text-danger">*</span></label>
                <select
                  className="w-full bg-elevated border border-border rounded px-2 py-1.5 text-sm"
                  value={cutoverGateway}
                  onChange={e => { setCutoverGateway(e.target.value); if (!cutoverTrunk) setCutoverTrunk(e.target.value); }}
                >
                  <option value="">-- select gateway --</option>
                  {trunks.map(t => (
                    <option key={t.gateway_uuid} value={t.gateway_uuid}>
                      {t.gateway_name}{t.gateway && t.gateway !== t.gateway_name ? ` (${t.gateway})` : ''}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-textSecondary mt-0.5">Carrier gateway for inbound DID routing.</div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Outbound trunk</label>
                <select
                  className="w-full bg-elevated border border-border rounded px-2 py-1.5 text-sm"
                  value={cutoverTrunk}
                  onChange={e => setCutoverTrunk(e.target.value)}
                >
                  <option value="">-- same as inbound gateway --</option>
                  {trunks.map(t => (
                    <option key={t.gateway_uuid} value={t.gateway_uuid}>
                      {t.gateway_name}{t.gateway && t.gateway !== t.gateway_name ? ` (${t.gateway})` : ''}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-textSecondary mt-0.5">Outbound routing trunk (defaults to inbound gateway).</div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Phone reprovision</label>
                <select
                  className="w-full bg-elevated border border-border rounded px-2 py-1.5 text-sm"
                  value={cutoverMode}
                  onChange={e => setCutoverMode(e.target.value)}
                >
                  <option value="">Auto-detect per phone (recommended)</option>
                  <option value="__local__">Local — force direct provisioning for all phones</option>
                  {sbcNodes.map(n => <option key={n.id} value={n.id}>Force all phones → {n.name}</option>)}
                </select>
                {cutoverMode === '' && (
                  phoneSbc === null ? (
                    <div className="text-xs text-textSecondary mt-1">Loading phone inventory…</div>
                  ) : phoneSbc.total === 0 ? (
                    <div className="text-xs text-textSecondary mt-1">No phones imported for this tenant.</div>
                  ) : (
                    <div className="rounded border border-border bg-elevated p-2 text-xs space-y-1 mt-1">
                      {phoneSbc.by_sbc.map((g, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-medium text-textPrimary">{g.count} phone{g.count !== 1 ? 's' : ''}</span>
                          <span className="text-textSecondary">
                            {g.sbc_node_id ? `via SBC: ${g.sbc_name || g.sbc_node_id}` : 'local / direct provisioning'}
                          </span>
                        </div>
                      ))}
                      {phoneSbc.unregistered > 0 && (
                        <div className="text-textSecondary">{phoneSbc.unregistered} not yet seen — will self-provision on first connect.</div>
                      )}
                    </div>
                  )
                )}
                <div className="text-xs text-textSecondary mt-0.5">
                  {cutoverMode === ''
                    ? 'Each phone is pushed via the SBC it actually enrolled through (from phone inventory) — handles multi-location departments.'
                    : cutoverMode === '__local__'
                      ? 'Overrides auto-detect: every phone is unbound from SBCs and provisions directly from Stratus.'
                      : 'Overrides auto-detect: every phone in this tenant is bound to and pushed via the selected SBC.'}
                </div>
              </div>
            </div>

            {cutoverErr && (
              <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <span className="break-words">{cutoverErr}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setCutoverDomain(null)} disabled={cutoverLoading} className="btn-ghost text-sm">
                Cancel
              </button>
              <button
                onClick={executeCutover}
                disabled={!cutoverGateway || cutoverLoading}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {cutoverLoading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {cutoverLoading ? 'Cutting over…' : 'Confirm cut over'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtBytes(n: number): string {
  if (!n || n < 0) return '0 MB';
  const mb = n / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 10) return `${Math.round(mb)} MB`;
  return `${mb.toFixed(1)} MB`;
}

// A DETERMINATE gauge for the one step whose progress is actually knowable — the
// upload. Fills to the real byte percentage.
function ProgressGauge({ pct, title, sub }: { pct: number; title: string; sub?: string }) {
  const p = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="card p-8 flex flex-col items-center gap-4 text-center">
      <div className="rounded-full bg-primary/15 p-3 text-primary"><Upload size={26} /></div>
      <div className="font-medium">{title}</div>
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-textSecondary tabular-nums">{sub}</span>
          <span className="tabular-nums font-semibold text-textPrimary">{p}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-elevated overflow-hidden" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out" style={{ width: `${p}%` }} />
        </div>
      </div>
    </div>
  );
}

// A "we're working" panel that never looks frozen: an indeterminate bar sweeps
// continuously (real progress is unknowable — parse/restore is a black box) and
// the sub-label rotates through the actual sub-steps so it reads as motion.
function WorkingBar({
  title, steps, children,
}: { title: string; steps: string[]; children?: ReactNode }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (steps.length <= 1) return;
    const t = setInterval(() => setI(n => (n + 1) % steps.length), 2400);
    return () => clearInterval(t);
  }, [steps.length]);
  return (
    <div className="card p-8 flex flex-col items-center gap-4 text-center">
      <Loader2 size={28} className="import-spinner text-primary" />
      <div className="font-medium">{title}</div>
      <div key={i} className="text-sm text-textSecondary min-h-[1.25rem] import-fade">
        {steps[i] || steps[0]}
      </div>
      <div className="w-full max-w-sm h-1.5 rounded-full bg-elevated overflow-hidden" role="progressbar" aria-label={title}>
        <div className="import-ind h-full rounded-full bg-primary" />
      </div>
      {children}
      <style>{`
        @keyframes import-spin {
          to { transform: rotate(360deg); }
        }
        .import-spinner { animation: import-spin 0.9s linear infinite; }
        @keyframes import-ind-slide {
          0%   { margin-left: -45%; width: 45%; }
          50%  { width: 65%; }
          100% { margin-left: 100%; width: 45%; }
        }
        .import-ind { animation: import-ind-slide 1.3s ease-in-out infinite; }
        @keyframes import-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .import-fade { animation: import-fade-in .5s ease; }
        @media (prefers-reduced-motion: reduce) {
          .import-spinner { animation: import-spin 3s linear infinite; }
          .import-ind { animation: import-ind-slide 4s ease-in-out infinite; }
          .import-fade { animation: none; }
        }
      `}</style>
    </div>
  );
}

function Stepper({ phase }: { phase: number }) {
  return (
    <div className="flex items-center">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const state: 'done' | 'active' | 'todo' = n < phase ? 'done' : n === phase ? 'active' : 'todo';
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border ${
                  state === 'done'
                    ? 'bg-primary border-primary text-white'
                    : state === 'active'
                    ? 'border-primary text-primary'
                    : 'border-border text-textSecondary'
                }`}
              >
                {state === 'done' ? <Check size={14} /> : n}
              </div>
              <span className={`text-sm whitespace-nowrap ${state === 'todo' ? 'text-textSecondary' : 'text-textPrimary'}`}>{label}</span>
            </div>
            {n < STEPS.length && <div className={`flex-1 h-px mx-3 ${n < phase ? 'bg-primary' : 'bg-border'}`} />}
          </div>
        );
      })}
    </div>
  );
}

function ToggleRow({
  checked, onChange, title, desc,
}: { checked: boolean; onChange: (v: boolean) => void; title: string; desc: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-1" />
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-textSecondary">{desc}</div>
      </div>
    </label>
  );
}

function ReportGrid({ report }: { report: ImportReport }) {
  const t = report.totals || {};
  const tiles: { label: string; value: number; icon: typeof Users }[] = [
    { label: 'Extensions', value: num(t.extensions_created), icon: Users },
    { label: 'Phones', value: num(t.devices), icon: Phone },
    { label: 'Ring groups', value: num(t.ring_groups), icon: Radio },
    { label: 'Trunks', value: num(t.gateways), icon: Cable },
    { label: 'DIDs', value: num(t.inbound_rules), icon: Hash },
    { label: 'Audio files', value: num(t.files_copied), icon: Music },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full">
      {tiles.map(t => {
        const Icon = t.icon;
        return (
          <div key={t.label} className="rounded-md border border-border bg-elevated p-3 flex items-center gap-3 text-left">
            <Icon size={18} className="text-primary shrink-0" />
            <div>
              <div className="text-lg font-semibold leading-none tabular-nums">{t.value}</div>
              <div className="text-xs text-textSecondary mt-1">{t.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Renumber conflict panel ────────────────────────────────────────────────────
// Shown when validate returns extension_collision blockers. Each row lets the
// operator confirm or change the suggested replacement number before running.
function RenumberPanel({
  blockers, renumberMap, onChange,
}: {
  blockers: Blocker[];
  renumberMap: Record<string, string>;
  onChange: (m: Record<string, string>) => void;
}) {
  return (
    <div className="card border-warning/40 bg-warning/5 p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle size={16} className="text-warning" />
        {blockers.length} extension conflict{blockers.length === 1 ? '' : 's'} — assign new numbers
      </div>
      <p className="text-sm text-textSecondary">
        These extensions are already in use. Assign replacement numbers below — ring groups, IVRs, and queues
        are updated automatically. Affected phones will need reprovisioning.
      </p>
      <div className="space-y-2">
        {blockers.map(b => (
          <div key={b.extension} className="flex items-center gap-3 rounded-md border border-border bg-elevated px-3 py-2">
            <div className="text-sm flex-1">
              <span className="font-mono font-semibold text-textPrimary">{b.extension}</span>
              <span className="text-textSecondary ml-2 text-xs">{b.reason}</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <RefreshCw size={13} className="text-warning" />
              <label className="sr-only">New extension number</label>
              <input
                type="text"
                inputMode="numeric"
                value={renumberMap[b.extension!] || ''}
                onChange={e => onChange({ ...renumberMap, [b.extension!]: e.target.value.replace(/\D/g, '') })}
                className="w-24 bg-background border border-border rounded px-2 py-1 text-sm font-mono text-center"
                placeholder={b.suggested_renumber || ''}
                aria-label={`New number for extension ${b.extension}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── DID review panel ──────────────────────────────────────────────────────────
// Groups DIDs by trunk (ExternalLine). Operator unchecks ones that don't belong
// to this department — only checked DIDs get imported.
function DidsPanel({
  dids, selected, onChange,
}: {
  dids: DidEntry[];
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
}) {
  const allChecked = selected.size === dids.length;
  const toggle = (did: string) => {
    const next = new Set(selected);
    if (next.has(did)) next.delete(did); else next.add(did);
    onChange(next);
  };
  const toggleAll = () => onChange(allChecked ? new Set() : new Set(dids.map(d => d.did)));

  // Group DIDs by trunk
  const trunks = new Map<string, { label: string; host: string | undefined; dids: DidEntry[] }>();
  for (const d of dids) {
    const key = d.external_line_id || '__none__';
    if (!trunks.has(key)) {
      // Prefer the gateway/host as the human label when trunk_name is absent or a
      // generic template value (e.g. "CONFIGURATION_TYPE" from some 3CX builds).
      const nameIsGeneric = !d.trunk_name || /^(CONFIGURATION_TYPE|SIP_TRUNK|PSTN_TRUNK)$/i.test(d.trunk_name.trim());
      const label = nameIsGeneric
        ? (d.trunk_host || d.trunk_name || (d.external_line_id ? `Trunk ${d.external_line_id}` : 'Unknown trunk'))
        : d.trunk_name;
      const host = nameIsGeneric ? undefined : (d.trunk_host || undefined);
      trunks.set(key, { label, host, dids: [] });
    }
    trunks.get(key)!.dids.push(d);
  }

  return (
    <div className="card border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold flex items-center gap-2">
          <Hash size={16} className="text-primary" />
          {dids.length} DID{dids.length === 1 ? '' : 's'} across {trunks.size} trunk{trunks.size === 1 ? '' : 's'}
        </div>
        <button onClick={toggleAll} className="text-xs btn-ghost text-textSecondary">
          {allChecked ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <p className="text-sm text-textSecondary">
        These are all trunks whose DIDs route to this department's extensions. Uncheck numbers that belong to a different office — only the checked DIDs and their trunks will be imported.
      </p>
      <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
        {[...trunks.entries()].map(([key, trunk]) => {
          const trunkSelected = trunk.dids.filter(d => selected.has(d.did)).length;
          const trunkAll = trunk.dids.length;
          const toggleTrunk = () => {
            const next = new Set(selected);
            if (trunkSelected === trunkAll) trunk.dids.forEach(d => next.delete(d.did));
            else trunk.dids.forEach(d => next.add(d.did));
            onChange(next);
          };
          return (
            <div key={key} className="rounded-md border border-border bg-elevated overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <div className="text-xs font-semibold text-textSecondary uppercase tracking-wide">{trunk.label}</div>
                <div className="flex items-center gap-2">
                  {trunk.host && <span className="text-xs text-textSecondary font-mono">{trunk.host}</span>}
                  <button onClick={toggleTrunk} className="text-xs btn-ghost py-0 text-textSecondary">
                    {trunkSelected === trunkAll ? 'Deselect trunk' : 'Select trunk'}
                  </button>
                </div>
              </div>
              <div className="divide-y divide-border">
                {trunk.dids.map(d => (
                  <label key={d.did} className="flex items-center gap-3 px-3 py-1.5 hover:bg-background cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(d.did)}
                      onChange={() => toggle(d.did)}
                    />
                    <span className="font-mono text-sm flex-1">{d.did}</span>
                    {d.destination && (
                      <span className="text-xs text-textSecondary">→ ext {d.destination}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-xs text-textSecondary pt-1">
        {selected.size} of {dids.length} selected
      </div>
    </div>
  );
}

// ── Reprovision list ───────────────────────────────────────────────────────────
// Phones whose extension changed need a SIP reprovision to pick up the new auth-id.
function ReprovisionList({ entries }: { entries: RenumberedEntry[] }) {
  if (!entries.length) return null;
  return (
    <div className="card border-warning/40 bg-warning/5 p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold">
        <Phone size={16} className="text-warning" />
        {entries.length} phone{entries.length === 1 ? '' : 's'} need reprovisioning
      </div>
      <p className="text-sm text-textSecondary">
        These extensions were renumbered. The phones bound to them must be reprovisioned
        to pick up the new extension / auth-id. Use the Cut Over panel below or reprovision
        manually from the Phone List.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="pb-2 pr-4 text-xs font-medium text-textSecondary">User</th>
              <th className="pb-2 pr-4 text-xs font-medium text-textSecondary">Old ext</th>
              <th className="pb-2 pr-4 text-xs font-medium text-textSecondary">New ext</th>
              <th className="pb-2 text-xs font-medium text-textSecondary">MACs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {entries.map((e, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-4 font-medium">{e.name || e.to}</td>
                <td className="py-1.5 pr-4 font-mono text-textSecondary line-through">{e.from}</td>
                <td className="py-1.5 pr-4 font-mono text-success">{e.to}</td>
                <td className="py-1.5 font-mono text-xs text-textSecondary">
                  {e.macs.length ? e.macs.join(', ') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportExtras({ report }: { report: ImportReport }) {
  const t = report.totals || {};
  const skippedAi = num(t.extensions_skipped);
  const skippedCollision = num((t as Record<string, unknown>).extensions_skipped_collision as number);
  const errors = Array.isArray(report.errors) ? report.errors : [];
  // Also surface per-group errors that weren't bubbled to top-level in older builds.
  const perGroupErrors: ImportError[] = [];
  if (Array.isArray(report.per_group)) {
    for (const g of report.per_group as Array<{ errors?: ImportError[]; group_code?: string }>) {
      if (Array.isArray(g.errors)) {
        for (const e of g.errors) {
          const key = `${g.group_code || ''}:${e.at || ''}:${e.error || ''}`;
          if (!errors.some(x => `${x.at || ''}:${x.error || ''}` === `${e.at || ''}:${e.error || ''}`)) {
            perGroupErrors.push({ at: g.group_code ? `${g.group_code} — ${e.at || ''}` : e.at, error: e.error });
          }
          void key;
        }
      }
    }
  }
  const allErrors = [...errors, ...perGroupErrors];

  if (!skippedAi && !skippedCollision && !allErrors.length) return null;

  return (
    <div className="space-y-3 border-t border-border pt-3">
      {skippedAi > 0 && (
        <div className="text-sm text-textSecondary">
          <span className="font-medium text-textPrimary">Skipped (AI agents):</span>{' '}
          {skippedAi} extension{skippedAi === 1 ? '' : 's'} left behind
        </div>
      )}
      {skippedCollision > 0 && (
        <div className="text-sm text-textSecondary">
          <span className="font-medium text-textPrimary">Skipped (number conflicts):</span>{' '}
          {skippedCollision} extension{skippedCollision === 1 ? '' : 's'} already owned by another tenant — imported without them
        </div>
      )}
      {allErrors.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium flex items-center gap-1 text-danger">
            <AlertTriangle size={14} /> Error log ({allErrors.length})
          </div>
          <div className="rounded-md border border-danger/30 bg-danger/5 p-3 space-y-1 max-h-64 overflow-y-auto">
            {allErrors.map((e, i) => (
              <div key={i} className="text-xs font-mono text-textSecondary break-words">
                {e.at && <span className="text-danger font-semibold">[{e.at}]</span>}{' '}
                {e.error || 'unknown error'}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
